import React from 'react';
import {MultiSelect, Select} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {DateInput} from '@mantine/dates';
import type {CourseClassroomStateEnum} from 'src/api/ProactApiClient';
import {PersonSelectBox} from 'src/app/databound-elements/PersonSelectBox';
import {useGetCourseDefinitionSelectOptionsQuery} from 'src/redux/queries/courseDefinition';
import {CourseClassroomStateDataOptions} from 'src/shared/constants/columnFilterData';
import type {ISelectOption} from 'src/types/Form';

export interface ScheduleFilters {
  courseDefinitionId?: string;
  instructorId?: string;
  states: CourseClassroomStateEnum[];
  startsAfter?: Date;
  startsBefore?: Date;
}

export const EMPTY_SCHEDULE_FILTERS: ScheduleFilters = {
  courseDefinitionId: undefined,
  instructorId: undefined,
  states: [],
  startsAfter: undefined,
  startsBefore: undefined,
};

export interface ScheduleFiltersBarProps {
  filters: ScheduleFilters;
  onChange: (filters: ScheduleFilters) => void;
}

/**
 * Filter controls for the cross-course Schedule page: course, instructor,
 * state and a start-date range — all supported server-side by
 * `GetCourseClassroomListRequestV2`.
 */
export const ScheduleFiltersBar: React.FC<ScheduleFiltersBarProps> = ({
  filters,
  onChange,
}) => {
  const {data: courseOptions} = useGetCourseDefinitionSelectOptionsQuery({
    start: 0,
    length: 500,
    sortColumn: 'name',
    sortDirection: 'asc',
  });

  const courseData = (courseOptions?.data ?? []).map(option => ({
    value: String(option.id),
    label: option.label ?? String(option.id),
  }));

  return (
    <Group gap="sm" mb="md" align="flex-end" wrap="wrap">
      <Box style={{width: '240px'}}>
        <Select
          label="Course"
          placeholder="All courses"
          data={courseData}
          value={filters.courseDefinitionId ?? null}
          onChange={value => onChange({...filters, courseDefinitionId: value ?? undefined})}
          searchable
          clearable
        />
      </Box>
      <Box style={{width: '220px'}}>
        <PersonSelectBox
          label="Instructor"
          queryOptions={{}}
          value={filters.instructorId}
          placeholder="All instructors"
          onSelectChangeCallback={(value: ISelectOption | null) =>
            onChange({
              ...filters,
              instructorId: value?.id ? String(value.id) : undefined,
            })
          }
        />
      </Box>
      <Box style={{width: '220px'}}>
        <MultiSelect
          label="State"
          placeholder="All states"
          data={CourseClassroomStateDataOptions}
          value={filters.states.map(String)}
          onChange={value =>
            onChange({
              ...filters,
              states: value.map(v => Number(v) as CourseClassroomStateEnum),
            })
          }
          clearable
        />
      </Box>
      <Box style={{width: '160px'}}>
        <DateInput
          label="Starts after"
          placeholder="Any date"
          value={filters.startsAfter ?? null}
          onChange={value => onChange({...filters, startsAfter: value ?? undefined})}
          clearable
        />
      </Box>
      <Box style={{width: '160px'}}>
        <DateInput
          label="Starts before"
          placeholder="Any date"
          value={filters.startsBefore ?? null}
          onChange={value => onChange({...filters, startsBefore: value ?? undefined})}
          clearable
        />
      </Box>
    </Group>
  );
};
